import { Link, useNavigate } from "react-router-dom";
import { Star, Zap, Heart, Rocket, Flame } from "lucide-react";


import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import { API } from "../lib/api";
import { hasInstantDelivery } from "../lib/product";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

export default function ProductCard({ product }) {
  const { has, toggle } = useWishlist();
  const { user } = useAuth();
  const navigate = useNavigate();
  const liked = has(product.id);
  const showInstantDelivery = hasInstantDelivery(product);
  // Flag dari backend: false = toko seller sedang tutup. Card tetap bisa
  // di-klik (buyer boleh lihat detail & chat), tapi tampilkan badge "Toko
  // Tutup" yang jelas dan redupkan card supaya user tahu produk tidak bisa
  // dibeli. Pembelian sebenarnya tetap diblokir di ProductDetail, Cart, dan
  // endpoint checkout.
  const isShopOpen = product.seller_is_open !== false;
  // Info flash sale dari backend (null kalau produk tidak sedang di-flash).
  // `product.price` tetap harga asli, harga yang dibayar ada di flash_price.
  const flash = product.flash_sale || null;


  const onHeart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user || user === false) {
      navigate("/login");
      return;
    }
    const res = await toggle(product.id);
    toast.success(res.in_wishlist ? "Added to wishlist" : "Removed from wishlist");
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      data-shop-open={isShopOpen ? "true" : "false"}
      className={`group block bg-[#0a0a0a] border transition-all duration-200 hover:-translate-y-1 ${
        isShopOpen
          ? "border-white/10 hover:border-[#00F0FF]/60"
          : "border-[#FF003C]/40 opacity-70 hover:opacity-90 hover:!border-[#FF003C]/60"
      }`}
    >
      <div className="aspect-square bg-black overflow-hidden relative">
        {product.image_url ? (
          <img
            src={resolveImg(product.image_url)}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0a0a0a] to-[#1a1a1a]" />
        )}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
          <span className="tag tag-accent">{product.type}</span>
          {/* Badge diskon flash sale supaya produk yang lagi promo tetap
              menonjol walau muncul di grid katalog biasa. */}
          {flash && (
            <span
              data-testid={`flash-badge-${product.id}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-[#FF003C] to-[#FF6B00] text-white text-[9px] md:text-[10px] font-black uppercase tracking-wider shadow-[0_0_12px_rgba(255,0,60,0.6)]"
            >
              <Flame className="w-3 h-3" /> -{flash.discount_percent}%
            </span>
          )}
        </div>


        {/* Badge "Toko Tutup" di pojok atas-kanan (sebelah wishlist) supaya
            user langsung tahu produk ini tidak bisa dibeli. Card tetap
            di-klik supaya user bisa lihat detail & chat seller. */}
        {!isShopOpen && (
          <div
            data-testid={`shop-closed-badge-${product.id}`}
            className="absolute top-1.5 right-1.5 md:top-2 md:right-12 z-30 inline-flex items-center gap-1 px-2 py-1 md:px-2.5 md:py-1 bg-[#FF003C] text-white text-[9px] md:text-[10px] font-black uppercase tracking-widest shadow-[0_0_12px_rgba(255,0,60,0.65)]"
          >
            <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white animate-pulse" />
            Toko Tutup
          </div>
        )}
        {showInstantDelivery && (
          <div className="absolute bottom-0 left-0 z-20 pointer-events-none">
            <div className="instant-delivery-wrapper relative">
              <div className="instant-delivery-ring absolute inset-0"></div>
              <div className="instant-delivery-badge relative inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2.5 bg-gradient-to-r from-[#FFD700] via-[#FF6B00] to-[#FF003C] text-black text-[10px] md:text-[13px] font-black uppercase tracking-wider">
                <span className="instant-delivery-sparkle absolute -top-1 -left-1 w-2 h-2 bg-white rounded-full"></span>
                <span className="instant-delivery-sparkle absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-white rounded-full" style={{animationDelay: '0.5s'}}></span>
                <Rocket className="w-3 h-3 md:w-4 md:h-4 animate-bounce shrink-0" strokeWidth={2.5} />
                <span className="leading-none whitespace-nowrap drop-shadow-sm">Pengiriman Instan</span>
                <span className="absolute -right-2 top-0 bottom-0 w-2 bg-black/20" style={{clipPath: 'polygon(0 0, 100% 50%, 0 100%)'}}></span>
              </div>
              <div className="instant-delivery-shimmer absolute inset-0 pointer-events-none overflow-hidden"></div>
            </div>
          </div>
        )}

        <button
          type="button"
          data-testid={`wishlist-toggle-${product.id}`}
          onClick={onHeart}
          aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute top-1.5 right-1.5 md:top-2 md:right-2 w-7 h-7 md:w-9 md:h-9 flex items-center justify-center border backdrop-blur-md transition-all ${
            liked
              ? "bg-[#FF003C]/80 border-[#FF003C] text-white"
              : "bg-black/40 border-white/15 text-white/80 hover:border-[#FF003C] hover:text-[#FF003C]"
          }`}
        >
          <Heart className={`w-3.5 h-3.5 md:w-4 md:h-4 ${liked ? "fill-current" : ""}`} />
        </button>
      </div>
      <div className="p-2.5 md:p-4">
        <h3 className="font-semibold text-xs md:text-sm text-white line-clamp-2 min-h-[32px] md:min-h-[40px] leading-snug group-hover:text-[#00F0FF] transition-colors">
          {product.title}
        </h3>
        <p className="text-[11px] md:text-xs text-white/50 mt-1 truncate">{product.seller_name}</p>
        <div className="mt-2 md:mt-3 flex items-center justify-between gap-1">
          {flash ? (
            <span className="min-w-0 flex flex-col leading-tight">
              <span className="font-display font-bold text-sm md:text-base text-[#FF003C] truncate">
                {formatIDR(flash.flash_price)}
              </span>
              <span className="text-[10px] md:text-[11px] text-white/40 line-through truncate">
                {formatIDR(product.price)}
              </span>
            </span>
          ) : (
            <span className="font-display font-bold text-sm md:text-base text-[#00F0FF] truncate">
              {formatIDR(product.price)}
            </span>
          )}

          <div className="flex items-center gap-1 text-[11px] md:text-xs text-white/60 shrink-0">
            <Star className="w-3 h-3 fill-[#FFD700] text-[#FFD700]" />
            <span>{product.rating?.toFixed(1) || "4.8"}</span>
          </div>
        </div>
        <div className="mt-1.5 md:mt-2 flex items-center justify-between gap-1 text-[9px] md:text-[10px] uppercase tracking-widest">
          <span className="flex items-center gap-1 text-white/40">
            <Zap className="w-2.5 h-2.5 md:w-3 md:h-3" /> {product.sold || 0} sold
          </span>
          {typeof product.stock === "number" && (
            <span className={product.stock > 0 ? "text-[#00FF66]" : "text-[#FF6B6B]"}>
              {product.stock > 0 ? `Stok ${product.stock}` : "Stok habis"}
            </span>
          )}
        </div>

      </div>
    </Link>
  );
}
